import { FC, useEffect, useMemo, useState } from 'react';

import { cva } from 'class-variance-authority';
import type { MultiValue } from 'react-select';
import Select from 'react-select/creatable';
import { useGetTags, useCreateTag } from 'ui/partials/hooks';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

export interface TagOption {
  readonly value: string;
  readonly label: string;
}

interface PROPS {
  label?: string;
  className?: string;
  defaultValue?: string[];
  onChange: (val: MultiValue<TagOption>) => void;
  hideLabel?: boolean;
  size?: SIZE;
  required?: boolean;
  placeholder?: string;
  setError?: (error: boolean) => void;
  disabled?: boolean;
}

const controlClasses = cva(
  ['w-full !bg-fill-secondary', 'text-sm', '!shadow-none'],
  {
    variants: {
      size: {
        [SIZE.Small]: ['py-0'],
        [SIZE.Medium]: ['py-[3px]'],
        [SIZE.Large]: ['py-[3px]'],
      },
    },
    defaultVariants: {
      size: SIZE.Medium,
    },
  },
);

const TagsField: FC<PROPS> = ({
  label,
  className,
  onChange,
  defaultValue,
  hideLabel,
  size,
  required,
  placeholder,
  setError,
  disabled,
}) => {
  const { data: tags } = useGetTags();
  const { mutate: createTag } = useCreateTag();

  const [value, setValue] = useState<TagOption[]>([]);
  const [input, setInput] = useState('');
  const [isErrorCreate, setIsErrorCreate] = useState(false);

  const tagOptions = useMemo(
    () => tags?.data?.map((tag) => ({ value: tag._id, label: tag.tag })) ?? [],
    [tags],
  );

  const _defaultValue = useMemo(
    () => tagOptions.filter((opt) => defaultValue?.includes(opt.value)),
    [defaultValue, tagOptions],
  );

  const handleChange = (val: MultiValue<TagOption>) => {
    setValue(val as TagOption[]);
    onChange(val);
    setInput('');
  };

  const handleInputChange = (e: string) => {
    setInput(e);
  };

  const handleCreateTag = () => {
    if (isErrorCreate) {
      return;
    }

    createTag(
      { tag: input, key: input },
      {
        onSuccess: (data) => {
          setValue((prev) => [
            ...prev,
            { label: input, value: data?.data?._id },
          ]);
          onChange([...value, { label: input, value: data?.data?._id }]);
          setInput('');
        },
      },
    );
  };

  useEffect(() => {
    if (tagOptions.length && !value.length) {
      setValue(tagOptions.filter((opt) => defaultValue?.includes(opt.value)));
    }
  }, [defaultValue, tagOptions, value.length]);

  useEffect(() => {
    const regex = new RegExp('^[a-zA-Z0-9 ]*$');

    if (input) {
      setIsErrorCreate(!regex.test(input));
      setError?.(!regex.test(input));
    }
  }, [input, setError]);

  return (
    <div className={cn(className)}>
      {!hideLabel && (
        <p className="text-sm font-semibold mb-2.5">
          {label ?? 'Tags'}
          {required ? (
            <span className="text-danger-base">*</span>
          ) : (
            <span className="text-xs text-content font-normal ml-0.5">
              (Optional)
            </span>
          )}
        </p>
      )}
      <Select
        defaultValue={_defaultValue}
        value={value}
        isMulti
        isDisabled={disabled}
        placeholder={placeholder ?? 'Select'}
        options={tagOptions}
        onChange={handleChange}
        isCreatable={isErrorCreate}
        onInputChange={handleInputChange}
        inputValue={input}
        classNames={{
          control: (state) =>
            cn(
              controlClasses({ size }),
              state.isFocused && !isErrorCreate
                ? '!border-primary-base'
                : '!border-separator-secondary',
              isErrorCreate && '!border-danger-base',
            ),
          menu: () => cn('py-2.5'),
          option: ({ isFocused, isSelected }) => {
            return cn(
              '!cursor-pointer px-5',
              '!text-sm text-content',
              'hover:!bg-transparent hover:font-semibold',
              isFocused ? '!text-primary-base' : '',
              isSelected && '',
              '!bg-transparent',
            );
          },
          multiValue: () =>
            cn(
              'py-1 px-2 !bg-fill-primary',
              '!flex !items-center !justify-between',
              '!border !border-separator-primary !rounded',
            ),
          multiValueLabel: () => cn('!text-sm !p-0 font-semibold'),
          multiValueRemove: () =>
            cn('!p-0 !h-[12px] !w-[12px] ml-0.5', 'flex-shrink-0'),
          indicatorSeparator: () => '!hidden',
        }}
        onCreateOption={handleCreateTag}
        isValidNewOption={() => !isErrorCreate && !!input}
        menuIsOpen={isErrorCreate ? false : undefined}
      />
      {isErrorCreate && (
        <p className="text-xs text-danger-base mt-1.5">
          Tags can't have spaces and special characters.
        </p>
      )}
    </div>
  );
};

export default TagsField;
